/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  Input,
  OnDestroy,
  computed,
  signal
} from '@angular/core';
import template from './pagination.component.html';
import {visiblePageNumbers} from './visible-page-numbers';

@Component({
  standalone: false,
  selector: 'adn-pagination',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdnPaginationComponent implements DoCheck, OnDestroy {
  @Input() list: any;

  readonly pageSize = signal(25);
  readonly totalCount = signal(0);
  readonly currentPage = signal(1);
  readonly pageQueryResolved = signal(true);
  readonly hasItems = signal(false);

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalCount() / Math.max(1, this.pageSize())))
  );
  readonly pageRangeStart = computed(() =>
    this.pageSize() * (this.currentPage() - 1) + 1
  );
  readonly pageRangeEnd = computed(() =>
    Math.min(this.pageSize() * this.currentPage(), this.totalCount())
  );
  readonly pageNumbers = computed(() =>
    visiblePageNumbers(this.currentPage(), this.totalPages())
  );

  private lastSyncKey = '';
  private destroyed = false;
  private syncScheduled = false;

  ngDoCheck(): void {
    this.scheduleSyncFromList();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  changePage(page: number): void {
    if (!this.list || page < 1 || page > this.totalPages() || page === this.list.currentPage) {
      return;
    }
    this.list.currentPage = page;
    this.list.changePage();
    this.scheduleSyncFromList(true);
  }

  /**
   * Snapshot list pagination fields into signals. Defer so list-helper fill
   * (pageSize/totalCount null→25) cannot NG0100 mid-CD.
   */
  private scheduleSyncFromList(force = false): void {
    const list = this.list;
    if (!list) {
      return;
    }
    const key = [
      list.pageSize,
      list.totalCount,
      list.currentPage,
      list.pageQuery?.$resolved,
      list.items?.length,
      list.results?.length
    ].join('|');
    if (!force && key === this.lastSyncKey) {
      return;
    }
    this.lastSyncKey = key;
    if (this.syncScheduled) {
      return;
    }
    this.syncScheduled = true;
    // Macrotask (not queueMicrotask): signal writes in a microtask re-enter the
    // current ApplicationRef.synchronize loop and can NG0103 under load.
    setTimeout(() => {
      this.syncScheduled = false;
      if (this.destroyed || this.list !== list) {
        return;
      }
      this.pageSize.set(list.pageSize == null ? 25 : list.pageSize);
      this.totalCount.set(list.totalCount || 0);
      this.currentPage.set(list.currentPage || 1);
      this.pageQueryResolved.set(!list.pageQuery || !!list.pageQuery.$resolved);
      this.hasItems.set(!!(list.items?.length || list.results?.length));
    }, 0);
  }
}
